import { apiUrl } from "utils/apiUrl";
import { Col, Divider, Form, Input, message, Modal, Row, Select } from "antd";
import { Rule } from "antd/lib/form";
import { roleApi } from "api/role.api";
import { staffApi } from "api/staff.api";
import { InputNumber } from "components/Input/InputNumber";
import { SingleImageUpload } from "components/Upload/SingleImageUpload";
import React, { useEffect, useImperativeHandle, useState } from "react";
import { ModalStatus } from "types/modal";
import { Role } from "types/role";
import { Staff } from "types/staff";

const rules: Rule[] = [{ required: true }];
const { Option } = Select;

interface StaffForm extends Staff {
  roleId?: number;
}

export interface StaffModal {
  handleCreate: () => void;
  handleUpdate: (Staff: Staff) => void;
}
interface StaffModalProps {
  onClose: () => void;
  onSubmitOk: () => void;
}

export const StaffModal = React.forwardRef(({ onClose, onSubmitOk }: StaffModalProps, ref) => {
  const [form] = Form.useForm<StaffForm>();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<ModalStatus>("create");
  const [selectedStaff, setSelectedStaff] = useState<Staff>();
  const [roles, setRoles] = useState<Role[]>([]);

  useImperativeHandle<any, StaffModal>(
    ref,
    () => ({
      handleCreate() {
        form.resetFields();
        setVisible(true);
        setStatus("create");
        setSelectedStaff(undefined);
      },
      handleUpdate(Staff: Staff) {
        form.setFieldsValue({
          ...Staff,
          roleId: Staff.role.id,
        });
        setVisible(true);
        setStatus("update");
        setSelectedStaff(Staff);
      },
    }),
    [],
  );

  useEffect(() => {
    fetchRole();
  }, []);

  const fetchRole = async () => {
    const res = await roleApi.findAll({ page: 1, limit: 0 });
    setRoles(res.data);
  };

  const createData = async () => {
    const valid = await form.validateFields();
    const data = {
      staff: {
        name: form.getFieldValue("name"),
        username: form.getFieldValue("username"),
        email: form.getFieldValue("email"),
        password: form.getFieldValue("password"),
        avatar: form.getFieldValue("avatar"),
        baseSalary: Number(form.getFieldValue("baseSalary")) || 0,
        commissionTargetRevenue: Number(form.getFieldValue("commissionTargetRevenue")) || 0,
        commissionRateBelowKpi: Number(form.getFieldValue("commissionRateBelowKpi")) || 0,
        commissionRateReachKpi: Number(form.getFieldValue("commissionRateReachKpi")) || 0,
        commissionRateExceedKpi: Number(form.getFieldValue("commissionRateExceedKpi")) || 0,
      },
      roleId: form.getFieldValue("roleId"),
    };

    setLoading(true);
    try {
      await staffApi.create(data);
      message.success("Tạo mới thành công!");
      handleClose();
      onSubmitOk();
    } finally {
      setLoading(false);
    }
  };

  const updateData = async () => {
    const valid = await form.validateFields();
    const data = {
      staff: {
        ...form.getFieldsValue(),
        baseSalary: Number(form.getFieldValue("baseSalary")) || 0,
        commissionTargetRevenue: Number(form.getFieldValue("commissionTargetRevenue")) || 0,
        commissionRateBelowKpi: Number(form.getFieldValue("commissionRateBelowKpi")) || 0,
        commissionRateReachKpi: Number(form.getFieldValue("commissionRateReachKpi")) || 0,
        commissionRateExceedKpi: Number(form.getFieldValue("commissionRateExceedKpi")) || 0,
      },
      roleId: form.getFieldValue("roleId"),
    };
    setLoading(true);
    try {
      await staffApi.update(selectedStaff?.id || 0, data);
      message.success("Cập nhật thành công!");
      handleClose();
      onSubmitOk();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setVisible(false);
    setSelectedStaff(undefined);
    onClose();
  };

  return (
    <Modal
      onCancel={handleClose}
      open={visible}
      title={status == "create" ? "Tạo mới nhân viên" : "Cập nhật nhân viên"}
      style={{ top: 20 }}
      width={700}
      cancelText="Đóng"
      confirmLoading={loading}
      onOk={() => {
        status == "create" ? createData() : updateData();
      }}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Username" name="username" rules={rules}>
              <Input disabled={status == "update"} placeholder="" />
            </Form.Item>
          </Col>

          {status == "create" && (
            <Col span={12}>
              <Form.Item label="Mật khẩu" name="password" rules={rules}>
                <Input placeholder="" />
              </Form.Item>
            </Col>
          )}

          <Col span={12}>
            <Form.Item label="Họ tên" name="name" rules={rules}>
              <Input placeholder="" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Email" name="email">
              <Input placeholder="" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Quyền" name="roleId" rules={rules}>
              <Select style={{ width: "100%" }}>
                {roles.map((role) => (
                  <Option key={role.id} value={role.id}>
                    {role.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" orientationMargin={0}>
          Cấu hình hoa hồng
        </Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Lương cơ bản" name="baseSalary">
              <InputNumber placeholder="0" suffix="đ" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Mục tiêu doanh số / tháng (KPI)" name="commissionTargetRevenue">
              <InputNumber placeholder="0" suffix="đ" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="% khi chưa đạt KPI" name="commissionRateBelowKpi">
              <Input type="number" step={0.1} suffix="%" placeholder="2.5" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="% trên phần đạt KPI" name="commissionRateReachKpi">
              <Input type="number" step={0.1} suffix="%" placeholder="4" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="% trên phần vượt KPI" name="commissionRateExceedKpi">
              <Input type="number" step={0.1} suffix="%" placeholder="6" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item shouldUpdate={true}>
          {() => {
            return (
              <Form.Item label="Avatar" name="avatar">
                <SingleImageUpload
                  onUploadOk={(path: string) => {
                    console.log("onUploadOk:", path);
                    form.setFieldsValue({
                      avatar: path,
                    });
                  }}
                  imageUrl={form.getFieldValue("avatar")}
                  uploadUrl={apiUrl("/v1/admin/staff/upload")}
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      </Form>
    </Modal>
  );
});
